// Modals du pipeline — création candidat (saisie RH manuelle, pas de
// page publique en v1, avec dédup live sur l'email), rejet à motif
// fermé (RecRejectModal du proto) et envoi d'email par modèle d'étape
// (Rec2EmailModal). L'email part en tant que l'utilisateur courant via
// Gmail — même mécanique que les relances pointage.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ENTITIES, REC_EMAIL_TEMPLATES, REC_MOTIFS, REC_SOURCES, recFillTemplate } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import {
  checkRecCandidate, useRecNegDismiss,
  type RecApplicationDTO, type RecCandidateCheckDTO, type RecCreateCandidateInput, type RecRequisitionDTO,
} from '../../api/hooks.js';
import { recStageOf } from './common.js';

// Héberge une modal au-dessus de l'espace candidat (r3-panel, z 340):
// portal body + île .recapp pour les tokens de thème.
export function RecModalHost({ dark, children }: { dark: boolean; children: React.ReactNode }) {
  return createPortal(
    <div className={`recapp rec-modalhost${dark ? ' theme-dark' : ''}`}>{children}</div>,
    document.body,
  );
}

const CV_MAX = 8 * 1024 * 1024;

// ── création candidat ───────────────────────────────────────
export function CandidateModal({ reqs, defaultReqId, busy, onClose, onSubmit }: {
  reqs: RecRequisitionDTO[];
  defaultReqId: string | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: RecCreateCandidateInput, cv: File | null) => void;
}) {
  const open = reqs.filter((r) => ['ouverte', 'validee'].includes(r.status));
  const [f, setF] = useState({
    requisitionId: defaultReqId && open.some((r) => r.id === defaultReqId) ? defaultReqId : open[0]?.id ?? '',
    firstName: '', lastName: '', email: '', phone: '', city: '', linkedin: '',
    expSummary: '', cvUrl: '', portfolioUrl: '', letterUrl: '',
    source: 'annonce', referredBy: '',
  });
  const [cv, setCv] = useState<File | null>(null);
  const [cvLink, setCvLink] = useState(false);
  const [over, setOver] = useState(false);
  const cvInput = useRef<HTMLInputElement>(null);
  const pickCv = (file: File | undefined) => {
    if (!file) return;
    if (file.size > CV_MAX) { toast.danger('Fichier trop lourd — 8 Mo maximum.'); return; }
    if (!/\.(pdf|docx?|png|jpe?g)$/i.test(file.name)) { toast.danger('PDF, Word, PNG ou JPEG seulement.'); return; }
    setCv(file);
  };
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [known, setKnown] = useState<RecCandidateCheckDTO | null>(null);
  const checkTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const set = (k: string, v: string) => setF((old) => ({ ...old, [k]: v }));

  // Dédup live: dès que l'email a une forme valide, on demande au
  // serveur si la personne est déjà connue.
  useEffect(() => {
    clearTimeout(checkTimer.current);
    const email = f.email.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) { setKnown(null); return; }
    checkTimer.current = setTimeout(() => {
      checkRecCandidate(email, f.requisitionId).then(setKnown).catch(() => setKnown(null));
    }, 350);
    return () => clearTimeout(checkTimer.current);
  }, [f.email, f.requisitionId]);

  useEffect(() => {
    if (known) setF((old) => ({ ...old, firstName: old.firstName || known.firstName, lastName: old.lastName || known.lastName, city: old.city || (known.city ?? '') }));
  }, [known]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t) && tags.length < 8) setTags([...tags, t]);
    setTagInput('');
  };
  const ok = f.requisitionId && f.firstName.trim() && f.lastName.trim() && /^\S+@\S+\.\S+$/.test(f.email.trim())
    && (f.source !== 'référé' || f.referredBy.trim());
  const submit = () => {
    if (!ok) return;
    onSubmit({
      requisitionId: f.requisitionId,
      firstName: f.firstName.trim(), lastName: f.lastName.trim(), email: f.email.trim(),
      phone: f.phone.trim() || undefined, city: f.city.trim() || undefined,
      linkedin: f.linkedin.trim() || undefined, expSummary: f.expSummary.trim() || undefined,
      tags,
      cvUrl: f.cvUrl.trim() || undefined, portfolioUrl: f.portfolioUrl.trim() || undefined,
      letterUrl: f.letterUrl.trim() || undefined,
      source: f.source, referredBy: f.referredBy.trim() || undefined,
    }, cv);
  };
  const dupeOnThisReq = known?.applications.some((a) => a.requisitionId === f.requisitionId);

  return (
    <div className="rec-modal-ov" onClick={onClose}>
      <div className="rec-modal" style={{ width: 'min(720px,94vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal-h"><h3>Nouveau candidat</h3><button className="rec-x" onClick={onClose}>✕</button></div>
        <div className="rec-modal-b">
          <div className="rec-lbl" style={{ marginTop: 0 }}>Poste</div>
          <div className="rec-entrow">
            {open.map((r) => {
              const ent = ENTITIES.find((e) => e.id === r.entityId);
              return (
                <button key={r.id} className={`rec-epill${f.requisitionId === r.id ? ' on' : ''}`} onClick={() => set('requisitionId', r.id)}>
                  <span className="d" style={{ background: ent?.color }}></span>{r.roleLabel} · {ent?.name}
                </button>
              );
            })}
          </div>
          <div className="rec-f2">
            <div className="rec-field"><label>Prénom</label><input value={f.firstName} onChange={(e) => set('firstName', e.target.value)} placeholder="Amine" /></div>
            <div className="rec-field"><label>Nom</label><input value={f.lastName} onChange={(e) => set('lastName', e.target.value)} placeholder="BENSALEM" /></div>
          </div>
          <div className="rec-f2">
            <div className="rec-field"><label>Email</label><input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="candidat@gmail.com" /></div>
            <div className="rec-field"><label>Téléphone</label><input value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+213 …" /></div>
          </div>
          {known && (
            <div className="rec-dedup">
              <span>♻️</span>
              <div>
                <b>Candidat déjà connu — {known.firstName} {known.lastName}.</b> Pas de doublon: cette saisie ajoutera une candidature à son dossier existant.
                {known.applications.map((a, i) => (
                  <div key={i}>· {a.roleLabel} ({a.year}) — {recStageOf(a.stage).lbl}{a.rejectMotif ? ` · ${a.rejectMotif}` : ''}</div>
                ))}
                {dupeOnThisReq && <b style={{ color: 'var(--badc)' }}>Déjà candidat sur ce poste — choisissez un autre poste.</b>}
              </div>
            </div>
          )}
          <div className="rec-f2">
            <div className="rec-field"><label>Ville</label><input value={f.city} onChange={(e) => set('city', e.target.value)} placeholder="Alger" /></div>
            <div className="rec-field"><label>Expérience (résumé carte)</label><input value={f.expSummary} onChange={(e) => set('expSummary', e.target.value)} placeholder="3 ans · Studio Pixem" /></div>
          </div>
          <div className="rec-f2">
            <div className="rec-field">
              <label>Source</label>
              <select value={f.source} onChange={(e) => set('source', e.target.value)}>
                {REC_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {f.source === 'référé'
              ? <div className="rec-field"><label>Référé par</label><input value={f.referredBy} onChange={(e) => set('referredBy', e.target.value)} placeholder="Nom du collègue" /></div>
              : <div className="rec-field"><label>LinkedIn (optionnel)</label><input value={f.linkedin} onChange={(e) => set('linkedin', e.target.value)} placeholder="https://linkedin.com/in/…" /></div>}
          </div>
          <div className="rec-field">
            <label>Compétences / tags</label>
            <div className="rec-taginput">
              {tags.map((t) => (
                <span key={t} className="rec-tagchip">{t}<button onClick={() => setTags(tags.filter((x) => x !== t))}>✕</button></span>
              ))}
              <input
                value={tagInput} placeholder={tags.length ? '' : 'After Effects, React… (Entrée)'}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }}
                onBlur={addTag}
              />
            </div>
          </div>
          <div className="rec-f2">
            <div className="rec-field">
              <label>CV</label>
              <div
                role="button" tabIndex={0}
                className={`rec-cvdrop${cv ? ' has' : ''}${over ? ' over' : ''}`}
                onClick={() => cvInput.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); cvInput.current?.click(); } }}
                onDragOver={(e) => { e.preventDefault(); setOver(true); }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => { e.preventDefault(); setOver(false); pickCv(e.dataTransfer.files?.[0]); }}
                data-testid="rec-cv-drop"
              >
                <span>{cv ? '📄' : '⤒'}</span>
                <span className="n">{cv ? cv.name : 'Déposer ou choisir le CV — PDF, Word, image · 8 Mo'}</span>
                {cv && <button className="x" aria-label="Retirer le CV" onClick={(e) => { e.stopPropagation(); setCv(null); }}>✕</button>}
              </div>
              <input ref={cvInput} type="file" hidden accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf"
                onChange={(e) => { pickCv(e.target.files?.[0]); e.target.value = ''; }} data-testid="rec-cv-input" />
              {cvLink || f.cvUrl
                ? <input className="rec-cvlink" value={f.cvUrl} onChange={(e) => set('cvUrl', e.target.value)} placeholder="ou lien Drive (optionnel) — https://drive.google.com/…" />
                : <div className="rec-cvlink"><button type="button" onClick={() => setCvLink(true)}>ou coller un lien Drive</button></div>}
            </div>
            <div className="rec-field"><label>Lien portfolio</label><input value={f.portfolioUrl} onChange={(e) => set('portfolioUrl', e.target.value)} placeholder="https://…" /></div>
          </div>
          <div className="rec-recap" style={{ marginTop: 14 }}>
            <div className="r"><span>Consentement</span><b>horodaté à la création (loi 18-07) — conservation 24 mois après clôture</b></div>
          </div>
          <div className="rec-modal-f">
            <button className="rec-ghost" onClick={onClose}>Annuler</button>
            <button className="rec-go" disabled={!ok || !!dupeOnThisReq || busy} onClick={submit}>Créer la candidature</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── rejet (motif fermé) ─────────────────────────────────────
export function RejectModal({ app, busy, onClose, onConfirm, forced }: {
  app: RecApplicationDTO;
  busy: boolean;
  onClose: () => void;
  onConfirm: (motif: string, note: string) => void;
  // v2 — motif imposé (échec du cas pratique, incohérence à la vérification).
  forced?: string | null;
}) {
  const [m, setM] = useState<string | null>(forced ?? null);
  const [note, setNote] = useState('');
  return (
    <div className="rec-modal-ov" onClick={onClose}>
      <div className="rec-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal-h"><h3>Rejeter {app.candidate.firstName} {app.candidate.lastName}</h3><button className="rec-x" onClick={onClose}>✕</button></div>
        <div className="rec-modal-b">
          {forced && <div className="rec-outband" style={{ marginTop: 0 }}>Motif imposé : <b>{forced}</b> — aucune dérogation.</div>}
          {!forced && <div className="rec-lbl" style={{ marginTop: 0 }}>Motif — obligatoire, liste fermée (alimente les analytics)</div>}
          {!forced && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {REC_MOTIFS.map((x) => (
              <button
                key={x} className={`rec-epill${m === x ? ' on' : ''}`}
                style={m === x ? { borderColor: 'var(--bad)', background: 'var(--bads)', color: 'var(--badc)', fontWeight: 700 } : undefined}
                onClick={() => setM(x)}
              >{x}</button>
            ))}
          </div>}
          <div className="rec-lbl">Note interne (optionnelle)</div>
          <textarea
            className="rec-refuse-ta" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Jamais envoyée au candidat"
            style={{ width: '100%', border: '1px solid var(--line2)', borderRadius: 11, background: 'var(--s1)', padding: '10px 12px', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--ink)', outline: 'none', resize: 'vertical' }}
          ></textarea>
          <div className="rec-modal-f" style={{ justifyContent: 'flex-end' }}>
            <button className="rec-ghost" onClick={onClose}>Annuler</button>
            <button className="rec-go" style={{ background: 'var(--bad)' }} disabled={!m || busy} onClick={() => m && onConfirm(m, note.trim())}>
              Rejeter la candidature
            </button>
          </div>
          <div className="rec-hint" style={{ marginTop: 10 }}>
            Un to-do « Réponse négative à envoyer » apparaît sur le tableau de bord — rien ne part automatiquement.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── email par modèle d'étape ────────────────────────────────
function Fill({ text, vars }: { text: string; vars: Record<string, string> }) {
  const parts = text.split(/(\{\{\w+\}\})/g);
  return (
    <>
      {parts.map((p, i) => {
        const m = p.match(/^\{\{(\w+)\}\}$/);
        return m ? <span key={i} className="r2-var">{vars[m[1]!] ?? m[1]}</span> : p;
      })}
    </>
  );
}

export function EmailModal({ app, req, meName, meEmail, busy, onClose, onSend }: {
  app: RecApplicationDTO;
  req: RecRequisitionDTO;
  meName: string;
  meEmail: string;
  busy: boolean;
  onClose: () => void;
  onSend: (templateId: string) => void;
}) {
  const ent = ENTITIES.find((e) => e.id === req.entityId);
  const vars = useMemo(() => ({
    prenom: app.candidate.firstName,
    poste: req.roleLabel,
    entite: ent?.name ?? 'SHIFTIN',
  }), [app, req, ent]);
  const hot = (t: { stages: string[] }) => t.stages.includes(app.stage);
  const tpls = useMemo(() => [...REC_EMAIL_TEMPLATES].sort((a, b) => (hot(b) ? 1 : 0) - (hot(a) ? 1 : 0)), [app.stage]); // eslint-disable-line react-hooks/exhaustive-deps
  // v2 — to-do « réponse négative » ouvert : le modèle nok d'abord.
  const [sel, setSel] = useState(app.negReply === 'open' ? 'nok' : tpls[0]!.id);
  const tpl = REC_EMAIL_TEMPLATES.find((t) => t.id === sel)!;
  const dismiss = useRecNegDismiss();
  const already = app.emails.filter((e) => e.templateId === sel && e.status === 'sent').length;
  return (
    <div className="rec-modal-ov" onClick={onClose}>
      <div className="rec-modal" style={{ width: 'min(880px,96vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal-h">
          <h3>Email à {app.candidate.firstName} {app.candidate.lastName}</h3>
          <span className="rec-stpill mute">modèles par étape</span>
          <button className="rec-x" onClick={onClose}>✕</button>
        </div>
        <div className="rec-modal-b">
          <div className="r2-mail">
            <div>
              <div className="rec-lbl" style={{ marginTop: 0 }}>Modèle</div>
              <div className="r2-list">
                {tpls.map((x) => (
                  <button key={x.id} className={`r2-tpl${sel === x.id ? ' on' : ''}`} onClick={() => setSel(x.id)}>
                    <b>{x.lbl}</b>
                    <span className="mt"><span className={`r2-step${hot(x) ? ' hot' : ''}`}>{x.step}{hot(x) ? ' · étape actuelle' : ''}</span></span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="rec-mailbox">
                <div className="rec-mail-chrome"><i></i><i></i><i></i></div>
                <div className="rec-mail-meta">
                  <span>De : <b>{meName}</b> &lt;{meEmail}&gt; — via votre Gmail</span>
                  <span>À : <b>{app.candidate.firstName} {app.candidate.lastName}</b> &lt;{app.candidate.email}&gt;</span>
                  <span>Objet : <b><Fill text={tpl.subj} vars={vars} /></b></span>
                </div>
                <div className="rec-mail-body">
                  {tpl.body.map((p, i) => <p key={i} style={{ margin: i ? '10px 0 0' : 0 }}><Fill text={p} vars={vars} /></p>)}
                  <div className="sig">{meName} · {vars.entite} — charte SHIFTIN, envoyé depuis SHIFTeam, tracé au journal</div>
                </div>
              </div>
              {already > 0 && (
                <div className="rec-dedup" style={{ marginTop: 12 }}>
                  <span>✉️</span><div>Ce modèle a déjà été envoyé {already > 1 ? `${already} fois` : 'une fois'} à ce candidat.</div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end', alignItems: 'center' }}>
                <span className="rec-hint">Variables remplies automatiquement depuis la fiche</span>
                {app.negReply === 'open' && (
                  <button className="rec-ghost" disabled={dismiss.isPending} onClick={() => dismiss.mutate({ id: app.id }, {
                    onSuccess: () => { toast.success('To-do « réponse négative » écarté — tracé au journal.'); onClose(); },
                    onError: (err) => toast.danger((err as { body?: { message?: string } }).body?.message ?? 'Impossible d’écarter.'),
                  })}>Écarter sans envoyer</button>
                )}
                <button className="rec-go" disabled={busy} onClick={() => onSend(sel)}>
                  {busy ? 'Envoi…' : `Envoyer « ${recFillTemplate(tpl.subj, vars)} »`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
